import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import { fetchOrgData, joinOrg, loadNewOrg } from '../../ducks/joinActions';

const FormContainer = styled('div')`
  width: 430px;
  height: auto;
  background-color: white;
  padding: 20px;
  border-radius: 16px;

  @media screen and (max-width: 600px) {
    width: 330px;
    height: auto;
    padding-bottom: 20px;
  }
`;

const OrganizationWrapper = styled('div')`
  padding: 20px 0 32px 0;
  gap: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TextWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FeedbackHeader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ErrorMessage = styled('div')`
  color: #ff4444;
  font-size: 14px;
  text-align: center;
  margin-top: 10px;
`;

const JoinContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [showSuccess, setShowSuccess] = useState(false);
  const { orgData } = useSelector((state) => state.joinOrganization.data);
  const {
    isLoading, joinSuccess, error,
  } = useSelector((state) => state.joinOrganization.ui);

  // Get token from query params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (token) {
      dispatch(fetchOrgData(token));
    }
  }, [dispatch, location.search]);

  const handleJoinAccount = () => {
    if (!orgData?.accountCode) return;

    dispatch(joinOrg(orgData.accountCode))
      .then(() => {
        setShowSuccess(true);
        setTimeout(() => {
          dispatch(loadNewOrg(orgData.accountCode));
        }, 2000);
      })
      .catch(() => {
        setShowSuccess(false);
      });
  };

  if (isLoading) {
    return (
      <Spinner />
    );
  }

  if (joinSuccess || showSuccess) {
    return (
      <FormContainer>
        <FeedbackHeader>
          <Icon
            icon='checkmark'
            size={25}
            color='#0DCC8A'
          />
          <Text styledTextProps={{ fontSize: '24px', fontWeight: '600', textAlign: 'center' }}>
            Successfully Joined!
          </Text>
        </FeedbackHeader>
        <Text styledTextProps={{ fontSize: '16px', textAlign: 'center', color: '#666666' }}>
          You have successfully joined
          {' '}
          {orgData?.accountName}
          .
          Redirecting to your workouts...
        </Text>
      </FormContainer>
    );
  }

  if (error) {
    return (
      <FormContainer>
        <FeedbackHeader>
          <Icon
            icon='warning'
            size={25}
            color='#FF6600'
          />
          <Text styledTextProps={{ fontSize: '24px', fontWeight: '600' }}>Sorry, something went wrong.</Text>
        </FeedbackHeader>
        <ErrorMessage>{error}</ErrorMessage>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Text styledTextProps={{ fontSize: '24px', fontWeight: '600' }}>Join Organization</Text>
      <OrganizationWrapper>
        <Text styledTextProps={{ fontSize: '16px' }}>Confirm the organization you want to add to your existing TeamBuildr account.</Text>
        <LogoCircle>
          {orgData?.accountLogoUrl && (
            <img src={orgData.accountLogoUrl} alt='Organization Logo' />
          )}
        </LogoCircle>
        <TextWrapper>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: '600' }}>Account Name</Text>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: '600' }}>
            {orgData?.accountName || 'Loading...'}
          </Text>
        </TextWrapper>
        <TextWrapper>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: '600' }}>Account Code</Text>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: '600' }}>
            {orgData?.accountCode || 'Loading...'}
          </Text>
        </TextWrapper>
      </OrganizationWrapper>
      <Button
        fullWidth
        bgColor='#0DCC8A'
        txtColor='white'
        fontSize='16px'
        styledTextProps={{ fontWeight: 'bold' }}
        styledWrapperProps={{ height: '56px' }}
        customBorderRadius='8px'
        noBorder
        name='Confirm Account'
        onClick={handleJoinAccount}
        disabled={isLoading}
      />
    </FormContainer>
  );
};

export default JoinContainer;
